import type { SVGProps } from "react";

/** Three bars rising left to right; the first `filled` are solid, the rest faint. */
function PriorityBars({ filled }: { filled: number }) {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          x={1.5 + i * 4.75}
          y={10 - i * 3.5}
          width="3.5"
          height={4.5 + i * 3.5}
          rx="1"
          fill="currentColor"
          stroke="none"
          opacity={i < filled ? 1 : 0.25}
        />
      ))}
    </>
  );
}

const PATHS = {
  check: <path d="M3 8.5l3 3 7-7" />,
  x: <path d="M4 4l8 8M12 4l-8 8" />,
  plus: <path d="M8 3v10M3 8h10" />,
  folder: <path d="M2 4.5A1.5 1.5 0 0 1 3.5 3h3l1.5 1.5h4.5A1.5 1.5 0 0 1 14 6v5.5a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 11.5z" />,
  home: <path d="M2.5 7.5L8 3l5.5 4.5V13a1 1 0 0 1-1 1h-3v-4h-3v4h-3a1 1 0 0 1-1-1z" />,
  users: (
    <>
      <circle cx="6" cy="5.5" r="2.5" />
      <path d="M1.5 13.5c.5-2.5 2.3-4 4.5-4s4 1.5 4.5 4M10.5 3.2a2.5 2.5 0 0 1 0 4.6M12 9.8c1.3.5 2.2 1.8 2.5 3.7" />
    </>
  ),
  settings: (
    <>
      <circle cx="8" cy="8" r="2" />
      <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M3.4 12.6l1.4-1.4M11.2 4.8l1.4-1.4" />
    </>
  ),
  shield: <path d="M8 1.8l5 2v4.1c0 3-2.1 5.4-5 6.3-2.9-.9-5-3.3-5-6.3V3.8z" />,
  user: (
    <>
      <circle cx="8" cy="5.5" r="2.8" />
      <path d="M2.5 14c.6-2.8 2.8-4.5 5.5-4.5s4.9 1.7 5.5 4.5" />
    </>
  ),
  bot: (
    <>
      <rect x="2.5" y="5" width="11" height="8" rx="2" />
      <path d="M8 5V2.8M6 9h.01M10 9h.01M1 8.5v2M15 8.5v2" />
      <circle cx="8" cy="2.3" r=".6" />
    </>
  ),
  ext: <path d="M9 2.5h4.5V7M13.5 2.5L7 9M11.5 9.5v4h-9v-9h4" />,
  copy: (
    <>
      <rect x="5" y="5" width="8.5" height="8.5" rx="1.5" />
      <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" />
    </>
  ),
  download: <path d="M8 2.5v8M4.5 7L8 10.5 11.5 7M2.5 13.5h11" />,
  down: <path d="M4 6l4 4 4-4" />,
  lock: (
    <>
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
    </>
  ),
  globe: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M2 8h12M8 2c1.8 2 1.8 10 0 12M8 2c-1.8 2-1.8 10 0 12" />
    </>
  ),
  logout: <path d="M6 2.5H3.5a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1H6M10.5 11l3-3-3-3M13.5 8H6" />,
  archive: (
    <>
      <rect x="2" y="3" width="12" height="3" rx="1" />
      <path d="M3 6v6.5a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6M6.5 9h3" />
    </>
  ),
  image: (
    <>
      <rect x="2" y="3" width="12" height="10" rx="1.5" />
      <circle cx="6" cy="6.5" r="1.2" />
      <path d="M14 11l-3.5-3.5L4 13" />
    </>
  ),
  test: (
    <>
      <path d="M6 2h4M6.5 2v4.5L3 13a1 1 0 0 0 .9 1.5h8.2A1 1 0 0 0 13 13L9.5 6.5V2" />
      <path d="M4.8 10h6.4" />
    </>
  ),
  bug: (
    <>
      <rect x="4.5" y="5" width="7" height="8.5" rx="3.5" />
      <path d="M8 5v8.5M2 7h2.5M11.5 7H14M2 11h2.5M11.5 11H14M5.5 3l1.2 2M10.5 3L9.3 5" />
    </>
  ),
  alert: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 5v3.5M8 11h.01" />
    </>
  ),
  menu: <path d="M2.5 4h11M2.5 8h11M2.5 12h11" />,
  book: (
    <path d="M2.5 3h4A1.5 1.5 0 0 1 8 4.5V14a1.5 1.5 0 0 0-1.5-1.5h-4zM13.5 3h-4A1.5 1.5 0 0 0 8 4.5V14a1.5 1.5 0 0 1 1.5-1.5h4z" />
  ),
  layers: <path d="M8 2l6 3-6 3-6-3zM2 8l6 3 6-3M2 11l6 3 6-3" />,
  play: <path d="M5 3l8 5-8 5z" />,
  edit: <path d="M10.5 2.5l3 3L6 13H3v-3z" />,
  blocked: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M4.2 11.8l7.6-7.6" />
    </>
  ),
  skip: <path d="M3.5 4l4 4-4 4M8.5 4l4 4-4 4" />,
  stale: <path d="M13 8a5 5 0 1 1-1.5-3.6M13 2.5V5h-2.5" />,
  inbox: <path d="M2 9h3.5l1 2h3l1-2H14M3.5 3h9L14 9v4H2V9z" />,
  list: <path d="M6 4h8M6 8h8M6 12h8M2.5 4h.01M2.5 8h.01M2.5 12h.01" />,
  left: <path d="M10 3L5 8l5 5" />,
  right: <path d="M6 3l5 5-5 5" />,
  up: <path d="M4 10l4-4 4 4" />,
  clip: (
    <path d="M13 7.5l-5.2 5.2a3 3 0 0 1-4.3-4.2l5.6-5.6a2 2 0 0 1 2.8 2.8L6.4 11.2a1 1 0 0 1-1.4-1.4l4.8-4.8" />
  ),
  flag: <path d="M3 14V2.5M3 3h8.5l-1.5 3 1.5 3H3" />,
  diff: <path d="M4.5 2v7M1.5 5.5h6M8.5 12h6M11.5 2.5l3 3-3 3" />,
  branch: (
    <>
      <circle cx="4.5" cy="3.5" r="1.5" />
      <circle cx="4.5" cy="12.5" r="1.5" />
      <circle cx="11.5" cy="5" r="1.5" />
      <path d="M4.5 5v6M11.5 6.5c0 2.5-3 3-7 3.5" />
    </>
  ),
  merge: (
    <>
      <circle cx="4" cy="3.5" r="1.5" />
      <circle cx="4" cy="12.5" r="1.5" />
      <circle cx="12" cy="12.5" r="1.5" />
      <path d="M4 5v6M12 11V6.5a2 2 0 0 0-2-2H7.5M9.5 2.5l-2 2 2 2" />
    </>
  ),
  message: <path d="M2.5 3.5h11v7.5h-6l-3 2.5V11h-2z" />,
  trash: <path d="M2.5 4.5h11M6 4.5V3h4v1.5M4 4.5l.7 9h6.6l.7-9" />,
  search: (
    <>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5L14 14" />
    </>
  ),
  clock: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.5V8l2.5 1.5" />
    </>
  ),
  bell: <path d="M4 11V7a4 4 0 0 1 8 0v4l1.5 1.5h-11zM6.5 14h3" />,
  chart: <path d="M2.5 13.5h11M4.5 11V8M8 11V4.5M11.5 11V6.5" />,
  trendUp: <path d="M2.5 11l4-4 2.5 2.5 4.5-5M10 4.5h3.5V8" />,
  trendDown: <path d="M2.5 5l4 4 2.5-2.5 4.5 5M10 11.5h3.5V8" />,
  send: <path d="M14 2L2 7l5 2 2 5 5-12zM7 9l3-3" />,
  terminal: <path d="M2.5 3.5h11v9h-11zM5 6.5l2 1.5-2 1.5M8.5 10h2.5" />,
  circle: <circle cx="8" cy="8" r="5" />,
  more: <path d="M3.5 8h.01M8 8h.01M12.5 8h.01" strokeWidth="2.6" />,
  zoomIn: (
    <>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.3 10.3L14 14M5 7h4M7 5v4" />
    </>
  ),
  zoomOut: (
    <>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.3 10.3L14 14M5 7h4" />
    </>
  ),
  key: (
    <>
      <circle cx="5.5" cy="10.5" r="3" />
      <path d="M7.6 8.4L13.5 2.5M11 5l2 2M12.5 3.5l1.5 1.5" />
    </>
  ),
  calendar: (
    <>
      <rect x="2" y="3" width="12" height="11" rx="1.5" />
      <path d="M2 6.5h12M5 1.8v2.4M11 1.8v2.4" />
    </>
  ),
  board: (
    <>
      <rect x="2" y="2.5" width="3.4" height="11" rx="1" />
      <rect x="6.3" y="2.5" width="3.4" height="7" rx="1" />
      <rect x="10.6" y="2.5" width="3.4" height="9" rx="1" />
    </>
  ),
  table: (
    <>
      <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
      <path d="M2 6h12M2 9.5h12M6 6v7.5" />
    </>
  ),
  link: <path d="M6.8 9.2a2.6 2.6 0 0 0 3.7 0l2.2-2.2a2.6 2.6 0 0 0-3.7-3.7l-.9.9M9.2 6.8a2.6 2.6 0 0 0-3.7 0L3.3 9a2.6 2.6 0 0 0 3.7 3.7l.9-.9" />,
  tag: (
    <>
      <path d="M2.5 2.5h5l6 6-5 5-6-6z" />
      <circle cx="5.3" cy="5.3" r=".9" />
    </>
  ),
  subtask: <path d="M4 2.5v6.5a2 2 0 0 0 2 2h6M9.5 8.5L12 11l-2.5 2.5" />,
  checklist: <path d="M2.5 4.2l1.3 1.3 2.2-2.4M2.5 10.2l1.3 1.3 2.2-2.4M8.5 4.5h5M8.5 10.5h5" />,
  reply: <path d="M6.5 3.5L2.5 7.5l4 4M2.5 7.5h6.5a4.5 4.5 0 0 1 4.5 4.5v.5" />,
  eyeOff: <path d="M2 2l12 12M6.6 6.7a2 2 0 0 0 2.7 2.7M4.2 4.3C2.9 5.2 2 6.5 1.5 8c1.2 3 3.8 4.8 6.5 4.8 1.2 0 2.4-.4 3.4-1M7 3.3c.3 0 .7-.1 1-.1 2.7 0 5.3 1.8 6.5 4.8-.3.8-.7 1.5-1.2 2.1" />,
  grip: (
    <>
      <circle cx="6" cy="4" r=".6" />
      <circle cx="10" cy="4" r=".6" />
      <circle cx="6" cy="8" r=".6" />
      <circle cx="10" cy="8" r=".6" />
      <circle cx="6" cy="12" r=".6" />
      <circle cx="10" cy="12" r=".6" />
    </>
  ),
  filter: <path d="M2 3h12l-4.5 5.5V13l-3-1.5V8.5z" />,
  sprint: <path d="M13 8a5 5 0 1 1-1.5-3.6M13 2.5v2.5h-2.5M8 5.5V8l1.8 1.2" />,
  stop: <rect x="4" y="4" width="8" height="8" rx="1.5" />,
  timer: (
    <>
      <circle cx="8" cy="9" r="5" />
      <path d="M8 9V6.5M6.5 2h3M12.2 4.6l.9-.9" />
    </>
  ),
  backlog: <path d="M2.5 3.5h11M2.5 6.5h11M2.5 9.5h7M2.5 12.5h5" />,
  // Task priority (Linear-style): a filled alert square for urgent, filled signal bars for the rest (unfilled bars
  // faint), dashes for none. Filled shapes, so they read at 12–14px.
  priorityUrgent: (
    <>
      <rect x="1.5" y="1.5" width="13" height="13" rx="3" fill="currentColor" stroke="none" />
      <path d="M8 4.6v4.2M8 11.4h.01" stroke="var(--surface)" strokeWidth="2" />
    </>
  ),
  priorityHigh: <PriorityBars filled={3} />,
  priorityMedium: <PriorityBars filled={2} />,
  priorityLow: <PriorityBars filled={1} />,
  priorityNone: <path d="M2.5 8h2M7 8h2M11.5 8h2" strokeWidth="1.6" />,
  at: (
    <>
      <circle cx="8" cy="8" r="2.5" />
      <path d="M10.5 8v1.2a1.8 1.8 0 0 0 3.5.3V8A6 6 0 1 0 11 13.2" />
    </>
  ),
} as const;

export type IconName = keyof typeof PATHS;

type Props = SVGProps<SVGSVGElement> & { name: IconName; size?: number };

export function Icon({ name, size = 16, className, ...rest }: Props) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={["shrink-0", className].filter(Boolean).join(" ")}
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}

/** The Mixla mark: a nail driven into an M. The M follows the text colour (so it works in both themes); the nail is the accent. */
function MarkShapes({ sparks }: { sparks: boolean }) {
  return (
    <>
      <g fill="currentColor" transform="translate(0.3 1.1)">
        <path d="M4 39c0-4.4 3-7.6 7.2-7.6 1.2 0 2.3.4 3.3 1L39.6 49c.9.6 1.2 1.6.9 2.6l-4.7 13.6c-.4 1.1-1.6 1.5-2.6.9l-4.3-2.9c-1.5-1-3.5 0-3.5 1.8V92.6c0 1.9-2 3-3.6 2L8 85.5c-2.5-1.6-4-4.3-4-7.2z" />
        <path d="M96.5 39c0-4.4-3-7.6-7.2-7.6-1.2 0-2.3.4-3.3 1L62.2 47.9c-.6.4-1.1.9-1.4 1.5L49.6 69.3c-.8 1.5.8 3.1 2.3 2.3l18.2-9.4c1.3-.7 2.9.3 2.9 1.8V92.6c0 1.9 2 3 3.6 2L92.5 85.5c2.5-1.6 4-4.3 4-7.2z" />
      </g>
      {/* Impact sparks: too fine at icon sizes, where they only add noise. */}
      {sparks && (
        <path d="M30.2 76.4L32.2 83M42.8 87.8l11.8-6.6" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      )}
      <g style={{ fill: "var(--accent)" }}>
        <path d="M55.2 19.4l14.2 5.4L36.6 86.4c-.6 1.1-2 .6-1.8-.6z" />
        <ellipse cx="66.9" cy="14.5" rx="19" ry="8.8" transform="rotate(22 66.9 14.5)" />
      </g>
    </>
  );
}

/**
 * "mixla" with the mark standing in for the "m". Sized by the font size: the M's body spans the cap height and sits on
 * the baseline, and the nail rises above like an ascender.
 */
export function Wordmark({ className }: { className?: string }) {
  return (
    <span className={`whitespace-nowrap font-bold tracking-tight ${className ?? ""}`}>
      <span className="sr-only">mixla</span>
      <svg
        viewBox="0 0 100 100"
        aria-hidden="true"
        className="inline-block"
        style={{ width: "1.1em", height: "1.1em", verticalAlign: "-0.045em", marginRight: "0.01em" }}
      >
        <MarkShapes sparks />
      </svg>
      <span aria-hidden="true">ixla</span>
    </span>
  );
}
